/**
 * @module ProductManager
 */
import ProductLayers from './ProductLayers.js';
import ProductLegends from './ProductLegends.js';
import ProductOverlays from './ProductOverlays.js';
import ProductPopups from './ProductPopups.js';
import { loadConfig, loadCss, unloadCss } from './utils/loadUtils.js';

/**
 * @classdesc
 * This class manages the addition and removal of NHC map products to a common 
 * map. Products can consist of multiple layers, controls, legends, and
 * overlays and are defined in configuration files.
 */

class ProductManager {
  /**
   * Create a new ProductManager instance
   * @param {object} map - The OpenLayers map object
   */
  constructor(map, language) {
    this.map = map;
    this.language = language;
    // Product configurations are stored with the product name as the key
    this.products = {};
    this.productLayers = new ProductLayers(this.map, this.language);
    this.productLegends = new ProductLegends(this.map);
    this.productOverlays = new ProductOverlays(this.map);
    this.productPopups = new ProductPopups(this.map);
  }

  /**
   * Set the language for the product manager
   * @param {string} newLanguage - The new language code (e.g., 'en', 'es')
   */
  setLanguage(newLanguage) {
    this.language = newLanguage;
    this.productLayers.setLanguage(newLanguage);
  }

  /**
   * Add multiple products to the product manager.
   * @param {array} configs - An array of product configurations.
   */
  async addProductsFromConfigs(configs) {
    for (const config of configs) {
      await this.addProduct(config);
    }
  }

  /**
   * Add all product components to the map.
   * @param {string} productId 
   */
  async addProduct(config) {
    // Add CSS, layers, legends, controls, and overlays
    await loadCss(config.css, `${config.product}-css`);
    await this.productLayers.addLayers(config.layers, config.product);
    await this.productLegends.addLegends(config);
    this.productOverlays.addOverlays(config.overlays);
    this.productPopups.addPopups(config);

    // Store product configuration
    this.products[config.product] = config;
  }

  /**
   * Remove all product components from the map.
   * @param {string} productId 
   * @returns 
   */
  async removeProduct(productId) {
    const config = this.products[productId];
    if (!config) return;

    // Remove CSS, layers, legends, controls, and overlays
    unloadCss(`${config.product}-css`);
    this.productLayers.removeLayers(config.layers);
    this.productLegends.removeLegends(config);
    this.productOverlays.removeOverlays(config.overlays);
    this.productPopups.removePopups(config);

    // Remove product configuration
    delete this.products[productId];
  }

  /**
   * Update a product by reloading it's layer data.
   * @param {string} productId 
   * @returns 
   */
  async updateProduct(productId) {
    const config = this.products[productId];
    if (!config) return;

    await this.productLayers.updateLayers(config.layers, productId);
  }

  /**
   * Reloads all active products.
   */
  async reloadAllProducts() {
    const activeProducts = Object.keys(this.products);
    for (const productId of activeProducts) {
      await this.removeProduct(productId);
      await this.addProduct(productId);
    }
  }

  /**
   * Reloads all active products, intelligently handling language changes.
   */
  async reloadLanguageProducts() {
    // The new language is already set via setLanguage()
    const newLanguage = this.language;
    let config = {};
    
    for (const productId in this.products) {

      // Step 1: Reload the config for this product
      const configUrl = `/maps/${productId}/${productId}.json`;
      config = await loadConfig(configUrl, newLanguage);
      this.products[config.product] = config;

      // Step 2: Update all layers that need to be updated.
      for (const layerId in config.layers) {
        const layerConfig = config.layers[layerId];

        if (!layerConfig || !layerConfig.languages) {
          continue; // Skip if config or languages property is missing
        }

        const supportedLanguages = layerConfig.languages;

        // Case 1: Layer is multilingual and supports the new language. Refresh it.
        if (supportedLanguages.length > 1 && supportedLanguages.includes(newLanguage)) {
          //console.log(`Updating layer '${layerConfig.id}' for language '${newLanguage}'`);
          // We need a way to tell the layer instance to update itself.
          await this.productLayers.updateLayer(layerConfig, productId);
        }
        // Case 2: Layer is language-specific. Toggle its visibility.
        else if (supportedLanguages.length === 1) {
          const isVisible = supportedLanguages.includes(newLanguage);
          console.log(`Setting visibility for layer '${layerId}' to '${isVisible}'`);
          olLayer.setVisible(isVisible);
        }
        // Case 3: languages is empty or layer doesn't fit other cases. Do nothing.
      }
    }
    // Step 3: Remove and re-add all the other components
    this.productLegends.removeLegends(config);
    this.productOverlays.removeOverlays(config.overlays);
    this.productPopups.removePopups(config);
    await this.productLegends.addLegends(config);
    this.productOverlays.addOverlays(config.overlays);
    this.productPopups.addPopups(config);
  }

}

export default ProductManager;